import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {
  BillingInformation,
  EndOrderDetails,
  Order,
  TaxVoucherSummary,
} from '@pedix-workspace/utils';

import { TranslocoDirective } from '@ngneat/transloco';
import {
  arcaBusinessVatConditionOptions,
  getArcaVatConditionName,
  getArcaDocumentTypeName,
  isBillingInformationArca,
  ArcaDocumentType,
} from '@pedix-workspace/integrations-arca';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import {
  CreateVoucherModalComponent,
  TaxVoucherActionsComponent,
} from '@pedix-workspace/pedixapp-electronic-billing';
import { IconBillingVoucherComponent } from '@pedix-workspace/angular-ui-icons';
import { Integration } from '@pedix-workspace/integrations-shared';
import { ApiV1_Establishment } from '@pedix-workspace/api-v1';

export type OrderListDetailRenderEvent = {
  row: Order;
  height: number;
};

@Component({
  selector: 'pxw-order-details-billing',
  templateUrl: './order-details-billing.component.html',
  styleUrls: ['./order-details-billing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslocoDirective,
    ButtonComponent,
    TaxVoucherActionsComponent,
    IconBillingVoucherComponent,
    CreateVoucherModalComponent,
  ],
})
export class OrderDetailsBillingComponent {
  readonly createVoucherModal = viewChild.required(CreateVoucherModalComponent);

  readonly orderDetails = input.required<EndOrderDetails>();
  readonly billingInformation = input.required<BillingInformation | null | undefined>();
  readonly editEnabled = input.required<boolean>();
  readonly canGenerateTaxVoucher = input.required<boolean>();
  readonly electronicBillingIntegration = input.required<Integration>();
  readonly establishment = input.required<ApiV1_Establishment>();

  readonly adminMode = input(false);

  readonly taxVoucherSummary = signal<TaxVoucherSummary | null>(null);

  readonly vatCondition = computed(() => {
    const billingInformation = this.billingInformation();

    if (isBillingInformationArca(billingInformation)) {
      return getArcaVatConditionName(billingInformation.vatCondition);
    }

    return billingInformation?.vatCondition || '--';
  });
  readonly billingName = computed(() => {
    const billingInformation = this.billingInformation();

    return billingInformation?.name || '--';
  });
  readonly billingDocument = computed(() => {
    const billingInformation = this.billingInformation();

    if (!billingInformation?.documentType || !billingInformation?.documentNumber) {
      return '--';
    }

    const documentTypeLabel = getArcaDocumentTypeName(
      billingInformation?.documentType as ArcaDocumentType,
    );

    return `${documentTypeLabel} ${billingInformation.documentNumber}`;
  });
  readonly documentType = computed(() => {
    const billingInformation = this.billingInformation();

    if (isBillingInformationArca(billingInformation)) {
      return getArcaDocumentTypeName(billingInformation.documentType);
    }

    return billingInformation?.documentType || '--';
  });
  readonly billingAddress = computed(() => {
    const billingInformation = this.billingInformation();

    return billingInformation?.address || '--';
  });
  readonly isVatConditionBusiness = computed(() => {
    return !!arcaBusinessVatConditionOptions.find(
      option => option.id === this.billingInformation()?.vatCondition,
    );
  });
  readonly requiredDataCompleted = computed(() => {
    const billingInformation = this.billingInformation();

    if (!billingInformation) {
      return false;
    }
    if (this.isVatConditionBusiness()) {
      return billingInformation.name && billingInformation.documentNumber;
    }
    return true;
  });
  readonly isNotCanceled = computed(() => {
    return this.orderDetails().status !== 'canceled';
  });

  constructor() {
    effect(
      () => {
        this.taxVoucherSummary.set(this.orderDetails().taxVoucherSummary || null);
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  async onGenerateTaxVoucher() {
    this.createVoucherModal().open([this.orderDetails()]);
  }
}
